/**
 * Typed wrappers around the Sprint 4 exam + capstone endpoints.
 *
 * Backend references:
 * - ``backend/caitapi/routers/exam.py``
 * - ``backend/caitapi/routers/capstone.py``
 *
 * Decisions encoded:
 * - DEC-0015: 50-item stratified draw — handled server-side; the client
 *   just renders ``items.length === 50`` and the score breakdown by
 *   ``domain_scores``.
 * - DEC-0016: cooldown surfaces the same way as the module-quiz —
 *   422 with ``detail.blocking === 'cooldown_active'``; we re-throw a
 *   typed error so ExamPage can render a countdown banner.
 * - DEC-0017: ``total_pause_seconds`` ships on the submit payload;
 *   server logs without enforcement.
 * - FR-G-1: capstone submit (status='submitted') is gated on a passing
 *   exam; we surface that 403 as a typed error too.
 */
import { apiGet, apiPost, apiPut } from "./api";
import type { KCClientItem, SubmitReviewItem } from "./lesson-api";

// ===========================================================================
// Exam types
// ===========================================================================

export interface StartExamResponse {
  attempt_id: string;
  attempt_number: number;
  items: KCClientItem[];
  passing_percent: number;
  domain_floor: Record<string, number>;
  total_seconds: number;
}

export interface SubmitExamResponse {
  attempt_id: string;
  attempt_number: number;
  score: number;
  passed: boolean;
  passing_percent: number;
  domain_scores: Record<string, number>;
  domain_floor: Record<string, number>;
  items: SubmitReviewItem[];
  cooldown_until: string | null;
}

interface CooldownDetail {
  blocking: "cooldown_active";
  cooldown_until: string;
  consecutive_failed_attempts: number;
  failure_cap: number;
  message: string;
}

interface ModuleQuizzesPendingDetail {
  blocking: "module_quizzes_pending";
  missing_modules: {
    id: string;
    sequence: number;
    slug: string;
    title: string;
  }[];
  message: string;
}

export class ExamCooldownError extends Error {
  readonly cooldown_until: Date;
  readonly consecutive_failed_attempts: number;
  readonly failure_cap: number;
  constructor(d: CooldownDetail) {
    super(d.message);
    this.name = "ExamCooldownError";
    this.cooldown_until = new Date(d.cooldown_until);
    this.consecutive_failed_attempts = d.consecutive_failed_attempts;
    this.failure_cap = d.failure_cap;
  }
}

export class ExamModuleGateError extends Error {
  readonly missing_modules: ModuleQuizzesPendingDetail["missing_modules"];
  constructor(d: ModuleQuizzesPendingDetail) {
    super(d.message);
    this.name = "ExamModuleGateError";
    this.missing_modules = d.missing_modules;
  }
}

function _extractDetail(err: unknown): unknown {
  const body = (err as { body?: unknown }).body;
  if (body && typeof body === "object") {
    return (body as { detail?: unknown }).detail;
  }
  return undefined;
}

export async function startExam(courseSlug: string): Promise<StartExamResponse> {
  try {
    return await apiPost<StartExamResponse>(
      `/api/courses/${encodeURIComponent(courseSlug)}/exam/start`,
    );
  } catch (err) {
    const detail = _extractDetail(err);
    if (detail && typeof detail === "object") {
      const d = detail as { blocking?: string };
      if (d.blocking === "cooldown_active") {
        throw new ExamCooldownError(detail as unknown as CooldownDetail);
      }
      if (d.blocking === "module_quizzes_pending") {
        throw new ExamModuleGateError(
          detail as unknown as ModuleQuizzesPendingDetail,
        );
      }
    }
    throw err;
  }
}

export interface RecordExamAnswerResponse {
  attempt_id: string;
  answered_count: number;
  total_items: number;
}

export function recordExamAnswer(
  attemptId: string,
  itemId: string,
  optionId: string,
): Promise<RecordExamAnswerResponse> {
  return apiPost<RecordExamAnswerResponse>(
    `/api/exam/${encodeURIComponent(attemptId)}/answer`,
    { item_id: itemId, option_id: optionId },
  );
}

export function submitExam(
  attemptId: string,
  totalPauseSeconds: number,
  answers: Record<string, string>,
): Promise<SubmitExamResponse> {
  // `answers` is the client's full {item_id: option_id} map. Sending it on
  // submit makes the submit authoritative for grading, so a per-answer
  // autosave dropped under a flaky connection doesn't cost the candidate a
  // question they actually answered.
  return apiPost<SubmitExamResponse>(
    `/api/exam/${encodeURIComponent(attemptId)}/submit`,
    { total_pause_seconds: totalPauseSeconds, answers },
  );
}

// ===========================================================================
// Capstone types
// ===========================================================================

export type CapstoneStatus = "draft" | "submitted" | "graded";

export interface CapstoneSubmission {
  id: string;
  attempt_number: number;
  body: string;
  word_count: number;
  status: CapstoneStatus;
  attachment_url: string | null;
  submitted_at: string | null;
  updated_at: string;
  word_count_min: number;
  word_count_max: number;
}

interface CapstoneExamGateDetail {
  blocking: "final_exam_not_passed";
  message: string;
}

export class CapstoneExamGateError extends Error {
  constructor(d: CapstoneExamGateDetail) {
    super(d.message);
    this.name = "CapstoneExamGateError";
  }
}

export async function writeCapstone(
  courseSlug: string,
  body: string,
  capstoneStatus: "draft" | "submitted",
  attachmentUrl: string | null = null,
): Promise<CapstoneSubmission> {
  try {
    return await apiPost<CapstoneSubmission>(
      `/api/courses/${encodeURIComponent(courseSlug)}/capstone`,
      { body, status: capstoneStatus, attachment_url: attachmentUrl },
    );
  } catch (err) {
    const detail = _extractDetail(err);
    if (detail && typeof detail === "object") {
      const d = detail as { blocking?: string };
      if (d.blocking === "final_exam_not_passed") {
        throw new CapstoneExamGateError(
          detail as unknown as CapstoneExamGateDetail,
        );
      }
    }
    throw err;
  }
}

export function listCapstones(courseSlug: string): Promise<CapstoneSubmission[]> {
  return apiGet<CapstoneSubmission[]>(
    `/api/courses/${encodeURIComponent(courseSlug)}/capstone`,
  );
}

export function editCapstoneDraft(
  submissionId: string,
  body: string,
  updatedAt: string,
  attachmentUrl: string | null = null,
): Promise<CapstoneSubmission> {
  return apiPut<CapstoneSubmission>(
    `/api/capstone/${encodeURIComponent(submissionId)}`,
    { body, updated_at: updatedAt, attachment_url: attachmentUrl },
  );
}
